import type {
  ChatCompletionContentPart,
  ChatCompletionUserMessageParam,
} from 'openai/resources';

import {
  CompletionStreamOptions,
  createCompletionStream,
} from './completion_stream';
import { MAX_TOOL_TURNS } from './config';
import { functionToolCalls } from './function_tool_calls';
import { hasCompleteArguments } from './has_complete_arguments';
import { LazyAsync, resolveLazyAsync } from './lazy_async';
import { Message, RawApiMessage, toMessages } from './message';
import { ToolCallFunction, ToolCallRunner } from './tool_call_runner';
import { ResolvedToolDefinition } from './tool_definition';

export interface ConversationContext {
  systemPrompt: string;
  tools: ResolvedToolDefinition[];
  model?: string;
  temperature?: number;
}

export interface ConversationState {
  messages: Message[];
  isProcessing: boolean;
}

export interface ConversationOptions {
  instanceId?: string;
  useAnonymously?: boolean;
  systemNotice?: () => Promise<string | null>;
}

// exported for test purposes only
export const SYSTEM_NOTICE_PROMPT_SUFFIX =
  'User messages may include <system-notice> tags. These contain information from the system. They bear no direct relation to the user messages in which they appear.';

// exported for testing only
export const UNFINISHED_TOOL_CALL_RESULT =
  'Error: the tool call did not finish.';

interface InternalState {
  rawApiMessages: RawApiMessage[];
  isProcessing: boolean;
  completedToolCallIds: ReadonlySet<string>;
}

export class Conversation {
  private state: InternalState;
  private listener: (state: ConversationState) => void;
  private readonly instanceId: string | undefined;
  private readonly useAnonymously: boolean;
  private readonly systemNotice?: () => Promise<string | null>;

  constructor(
    listener: (state: ConversationState) => void,
    initialMessages: RawApiMessage[] = [],
    {
      instanceId,
      useAnonymously = false,
      systemNotice,
    }: ConversationOptions = {},
  ) {
    this.listener = listener;
    this.instanceId = instanceId;
    this.useAnonymously = useAnonymously;
    this.systemNotice = systemNotice;

    this.state = {
      rawApiMessages: initialMessages,
      isProcessing: false,
      completedToolCallIds: new Set(),
    };

    this.emitState();
  }

  getRawApiMessages(): RawApiMessage[] {
    return this.state.rawApiMessages;
  }

  async processUserMessage(
    lazyAsyncContext: LazyAsync<ConversationContext>,
    userInput: ChatCompletionUserMessageParam['content'],
  ): Promise<void> {
    // TODO this method is not concurrency safe yet
    // (e.g. when the user sends multiple messages in quick succession)
    const userMessage: RawApiMessage = { role: 'user', content: userInput };
    const prevMessages = this.state.rawApiMessages;

    // make sure the app quickly shows the user message
    this.setRawApiMessagesWhileProcessing([...prevMessages, userMessage]);

    const notice = this.systemNotice ? await this.systemNotice() : null;

    const developerMessage: RawApiMessage | null = notice
      ? { role: 'developer', content: notice }
      : null;

    const newMessagesFromUser = developerMessage
      ? [userMessage, developerMessage]
      : [userMessage];

    if (developerMessage) {
      this.setRawApiMessagesWhileProcessing([
        ...prevMessages,
        ...newMessagesFromUser,
      ]);
    }

    const { systemPrompt, tools, temperature, model } =
      await resolveLazyAsync(lazyAsyncContext);

    const fullSystemPrompt = this.systemNotice
      ? `${systemPrompt}\n\n${SYSTEM_NOTICE_PROMPT_SUFFIX}`
      : systemPrompt;

    const messages = [
      ...getPrevMessages(prevMessages, fullSystemPrompt),
      ...newMessagesFromUser,
    ];

    const options: CompletionStreamOptions = {
      instanceId: this.instanceId,
      tools,
      model,
      temperature,
      useAnonymously: this.useAnonymously,
    };

    try {
      let allMessages = messages;

      for (let turns = 0; turns < MAX_TOOL_TURNS; turns++) {
        const nextMessages = await this.runTurn(allMessages, options);
        if (!nextMessages) return;

        allMessages = nextMessages;
      }
    } catch (error) {
      this.setRawApiMessagesWhileProcessing(
        answerUnfinishedToolCalls(this.state.rawApiMessages),
      );

      throw error;
    } finally {
      this.setState({ ...this.state, isProcessing: false });
    }
  }

  private async runTurn(
    messages: RawApiMessage[],
    options: CompletionStreamOptions,
  ): Promise<RawApiMessage[] | undefined> {
    let allMessages = messages;
    let currentMessage: RawApiMessage | undefined;

    const runner = new ToolCallRunner(options.tools, (index) => {
      const toolCall =
        currentMessage && functionToolCalls(currentMessage)[index];

      // as before, a call with incomplete arguments gets no tool message (its result is discarded), so it does not count as completed
      if (toolCall && hasCompleteArguments(toolCall)) {
        this.addCompletedToolCall(toolCall.id);
      }
    });

    try {
      const completions = createCompletionStream(
        mergeDeveloperNotices(allMessages),
        options,
      );

      for await (currentMessage of completions) {
        this.setRawApiMessagesWhileProcessing([...allMessages, currentMessage]);

        runner.runToolCalls(
          toToolCallFunctions(currentMessage, { isPartial: true }),
        );
      }

      // the completion service yielded no answer, so there is nothing to continue with
      if (!currentMessage) return;

      runner.runToolCalls(
        toToolCallFunctions(currentMessage, { isPartial: false }),
      );

      allMessages = [...allMessages, currentMessage];
      const functionCalls = functionToolCalls(currentMessage);

      for await (const { index, result } of runner.awaitEach()) {
        const toolCall = functionCalls[index];

        // an incomplete tool call is not sent back to the model, so it can't have a result
        if (!hasCompleteArguments(toolCall)) continue;

        allMessages = [
          ...allMessages,
          { role: 'tool', tool_call_id: toolCall.id, content: result },
        ];

        this.setRawApiMessagesWhileProcessing(allMessages);
      }

      const hasAppendedToolMessage =
        allMessages[allMessages.length - 1]?.role === 'tool';

      // when the answer called no tool, undefined ends the run
      return hasAppendedToolMessage ? allMessages : undefined;
    } finally {
      // whatever ends the turn, no more arguments will arrive: ending the stream lets the running tool finish instead of waiting forever
      runner.cleanup();
    }
  }

  private addCompletedToolCall(toolCallId: string) {
    this.setState({
      ...this.state,
      completedToolCallIds: new Set([
        ...this.state.completedToolCallIds,
        toolCallId,
      ]),
    });
  }

  private setRawApiMessagesWhileProcessing(rawApiMessages: RawApiMessage[]) {
    this.setState({ ...this.state, rawApiMessages, isProcessing: true });
  }

  private setState(state: InternalState) {
    this.state = state;
    this.emitState();
  }

  private emitState() {
    this.listener({
      messages: toMessages(
        this.state.rawApiMessages,
        this.state.completedToolCallIds,
      ),
      isProcessing: this.state.isProcessing,
    });
  }
}

function toToolCallFunctions(
  message: RawApiMessage,
  { isPartial }: { isPartial: boolean },
): ToolCallFunction[] {
  const functionCalls = functionToolCalls(message);

  return functionCalls.map((toolCall, index) => ({
    name: toolCall.function.name,
    args: toolCall.function.arguments,
    // only the last tool call of a partial answer can still be incomplete
    isPartial: isPartial && index === functionCalls.length - 1,
  }));
}

function answerUnfinishedToolCalls(messages: RawApiMessage[]): RawApiMessage[] {
  const lastAssistantMessageIndex = messages
    .map((m) => m.role)
    .lastIndexOf('assistant');

  const hasAssistantMessage = lastAssistantMessageIndex > -1;
  const followingMessages = messages.slice(lastAssistantMessageIndex + 1);

  // a tool message must directly follow its tool call, otherwise skip answering
  if (
    !hasAssistantMessage ||
    followingMessages.some((m) => m.role !== 'tool')
  ) {
    return messages;
  }

  const answeredIds = new Set(
    followingMessages.flatMap((m) =>
      m.role === 'tool' ? [m.tool_call_id] : [],
    ),
  );

  const unfinishedResults: RawApiMessage[] = functionToolCalls(
    messages[lastAssistantMessageIndex],
  )
    .filter(
      (toolCall) =>
        hasCompleteArguments(toolCall) && !answeredIds.has(toolCall.id),
    )
    .map((toolCall) => ({
      role: 'tool',
      tool_call_id: toolCall.id,
      content: UNFINISHED_TOOL_CALL_RESULT,
    }));

  return [...messages, ...unfinishedResults];
}

function getPrevMessages(
  prevMessages: RawApiMessage[],
  systemPrompt: string,
): RawApiMessage[] {
  if (prevMessages.some((m) => m.role === 'user')) return prevMessages;

  return [
    {
      role: 'system',
      // TODO move computeSystemMessage up
      content: systemPrompt,
    },
    ...prevMessages,
  ];
}

function mergeDeveloperNotices(messages: RawApiMessage[]): RawApiMessage[] {
  // The underlying GenAI service does not accept `role: 'developer'`
  return messages.flatMap((message, index) => {
    if (message.role === 'developer') return [];

    const nextMessage = messages[index + 1];

    if (
      message.role !== 'user' ||
      nextMessage?.role !== 'developer' ||
      typeof nextMessage.content !== 'string'
    ) {
      return [message];
    }

    const mergedContent: ChatCompletionContentPart[] = [
      ...userContentAsParts(message.content),
      {
        type: 'text',
        text: `<system-notice>\n${nextMessage.content}\n</system-notice>`,
      },
    ];

    return [{ role: 'user', content: mergedContent }];
  });
}

function userContentAsParts(
  content: ChatCompletionUserMessageParam['content'],
): ChatCompletionContentPart[] {
  if (typeof content === 'string') return [{ type: 'text', text: content }];

  return [...content];
}
